import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api from "../../services/api";
import { Panel, Badge, Stat, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, cls, can } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Users, Languages, ConciergeBell, FileText } from "lucide-react";
import { ujmsCreateJuryLetter, ujmsJuryLetters, ujmsOpenJuryLetterPdf } from "../../services/ujmsApi";

const U = "/ujms";
const TABS = ["Jury Administration", "Interpreters", "Service Requests"];

export default function CourtServices() {
  const { access } = useOutletContext();
  const tabs = [
    ...(can(access, "jury.manage") ? ["Jury Administration"] : []),
    ...(can(access, "service.manage") ? ["Interpreters"] : []),
    ...(can(access, "service.request", "service.manage") ? ["Service Requests"] : []),
  ];
  const [tab, setTab] = useState(tabs[0]);
  return (
    <div className="space-y-4" data-testid="court-services">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Court Services</h1>
        <p className="text-sm text-slate-500">Jury administration, interpreter coordination and support service requests.</p>
      </div>
      <div className="flex gap-1">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`services-tab-${t.split(" ")[0].toLowerCase()}`}
            className={cls("text-xs font-semibold uppercase tracking-wide px-3 py-1.5 border rounded-sm",
              tab === t ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100")}>
            {t}
          </button>
        ))}
      </div>
      {tab === "Jury Administration" && <JuryTab access={access} />}
      {tab === "Interpreters" && <InterpreterTab access={access} />}
      {tab === "Service Requests" && <ServiceTab access={access} />}
    </div>
  );
}

function JuryTab({ access }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState({});
  const [panelF, setPanelF] = useState({});
  const [letterJuror, setLetterJuror] = useState(null);
  const load = useCallback(() => api.get(`${U}/jury/dashboard`).then((r) => setData(r.data)).catch(() => setData({ denied: true })), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); } };
  if (!data) return null;
  if (data.denied) return <Panel title="Jury administration" testId="jury-denied"><div className="p-4 text-sm text-slate-500">Your role does not include jury administration.</div></Panel>;
  const sc = data.status_counts;
  return (
    <div className="space-y-4" data-testid="jury-tab">
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {["POOL", "SUMMONED", "RESPONDED", "DEFERRED", "EXCUSED", "PANELED"].map((s) => (
          <Stat key={s} label={s} value={sc[s] || 0} testId={`jury-stat-${s.toLowerCase()}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Juror pool" testId="jury-pool" action={
          <div className="flex gap-1.5">
            <input className={`${inputCls} w-40`} placeholder="New juror name…" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="juror-name-input" />
            <button className={btnPrimary} onClick={() => run(() => api.post(`${U}/jury/jurors`, { name: f.name }), "Juror added to pool") && setF({})} data-testid="juror-add-btn"><Users className="w-4 h-4" /> Add</button>
          </div>}>
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>#</th><th className={thCls}>Name</th><th className={thCls}>Status</th><th className={thCls}>Report</th><th className={thCls}></th></tr></thead>
            <tbody>{data.jurors.slice(0, 20).map((j) => (
              <tr key={j.id} data-testid={`juror-row-${j.juror_number}`}>
                <td className={`${tdCls} font-mono text-xs`}>{j.juror_number}</td>
                <td className={tdCls}>{j.name}</td>
                <td className={tdCls}><Badge value={j.status} /></td>
                <td className={`${tdCls} font-mono text-xs`}>{j.report_date || "—"}</td>
                <td className={`${tdCls} whitespace-nowrap`}>
                  {["POOL", "DEFERRED"].includes(j.status) && <button className={btnGhost} onClick={() => run(() => api.post(`${U}/jury/jurors/${j.id}/summon`, { report_date: new Date(Date.now() + 12096e5).toISOString().slice(0, 10) }), "Summons issued")} data-testid={`juror-summon-${j.juror_number}`}>Summon</button>}
                  {j.status === "SUMMONED" && (
                    <>
                      <button className={`${btnGhost} ml-1`} onClick={() => run(() => api.post(`${U}/jury/jurors/${j.id}/respond`, { response: "WILL_APPEAR" }), "Response recorded")} data-testid={`juror-respond-${j.juror_number}`}>Will Appear</button>
                      <button className={`${btnGhost} ml-1`} onClick={() => run(() => api.post(`${U}/jury/jurors/${j.id}/respond`, { response: "REQUEST_EXCUSE", reason: "Hardship" }), "Excused")} data-testid={`juror-excuse-${j.juror_number}`}>Excuse</button>
                    </>
                  )}
                  <button className={`${btnGhost} ml-1 border-ujms text-ujms`} onClick={() => setLetterJuror(j)} data-testid={`juror-letter-${j.juror_number}`}>
                    <FileText className="w-3.5 h-3.5" /> Service Letter
                  </button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </Panel>
        <Panel title="Jury panels" testId="jury-panels" action={
          <div className="flex gap-1.5">
            <input className={`${inputCls} w-36`} placeholder="Panel name…" value={panelF.name || ""} onChange={(e) => setPanelF({ ...panelF, name: e.target.value })} data-testid="panel-name-input" />
            <button className={btnPrimary} onClick={() => run(() => api.post(`${U}/jury/panels`, { name: panelF.name, date: new Date().toISOString().slice(0, 10), size: 12 }), "Panel created")} data-testid="panel-create-btn">Create Panel</button>
          </div>}>
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>#</th><th className={thCls}>Name</th><th className={thCls}>Date</th><th className={thCls}>Seated</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
            <tbody>{data.panels.map((p) => (
              <tr key={p.id} data-testid={`panel-row-${p.panel_number}`}>
                <td className={`${tdCls} font-mono text-xs`}>{p.panel_number}</td>
                <td className={tdCls}>{p.name}</td>
                <td className={`${tdCls} font-mono text-xs`}>{p.date}</td>
                <td className={`${tdCls} font-mono text-xs`}>{p.assigned}/{p.size}</td>
                <td className={tdCls}><Badge value={p.status} /></td>
                <td className={tdCls}>
                  <button className={btnGhost} data-testid={`panel-fill-${p.panel_number}`}
                    onClick={() => run(() => api.post(`${U}/jury/panels/${p.id}/assign`, { juror_ids: data.jurors.filter((j) => j.status === "RESPONDED").map((j) => j.id) }), "Eligible jurors seated")}>Seat Responded</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </Panel>
      </div>
      <JuryLetterDialog juror={letterJuror} onClose={() => setLetterJuror(null)} />
    </div>
  );
}

function JuryLetterDialog({ juror, onClose }) {
  const [dates, setDates] = useState("");
  const [timeFrom, setTimeFrom] = useState("08:30");
  const [timeTo, setTimeTo] = useState("16:30");
  const [issuedTo, setIssuedTo] = useState("");
  const [body, setBody] = useState("");
  const [edited, setEdited] = useState(false);
  const [prev, setPrev] = useState([]);
  const [busy, setBusy] = useState(false);

  const fmt = (d) => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : "");
  const template = useCallback(() => {
    if (!juror || !dates) return "";
    return `TO WHOM IT MAY CONCERN:

This is to certify that ${juror.name}, Juror No. ${juror.juror_number}, reported for and performed jury service for the Circuit Court of Cook County, Illinois on ${dates}. The above-named juror was present at court from ${timeFrom} to ${timeTo} on each date of service.${issuedTo ? ` This certification is issued for presentation to ${issuedTo}.` : ""}

Under Illinois law (705 ILCS 305/4.1), an employer may not discharge, threaten, or otherwise coerce an employee because the employee receives a jury summons, responds to it, or serves as a juror. Please excuse this absence from work, school, or other obligations accordingly.`;
  }, [juror, dates, timeFrom, timeTo, issuedTo]);

  useEffect(() => {
    if (!juror) return;
    setDates(fmt(juror.report_date) || fmt(new Date().toISOString().slice(0, 10)));
    setTimeFrom("08:30"); setTimeTo("16:30"); setIssuedTo(""); setBody(""); setEdited(false);
    ujmsJuryLetters(juror.id).then((r) => setPrev(r.data)).catch(() => setPrev([]));
  }, [juror]);
  useEffect(() => { if (juror && !edited) setBody(template()); }, [juror, edited, template]);

  if (!juror) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await ujmsCreateJuryLetter(juror.id, {
        service_dates: dates, time_from: timeFrom, time_to: timeTo, issued_to: issuedTo, body_text: body });
      toast.success(`Jury service letter ${r.data.letter_number} issued`);
      ujmsOpenJuryLetterPdf(r.data.id, `${r.data.letter_number} — ${juror.name}`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="jury-letter-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Jury Service Letter — {juror.name}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">Form JU99 style · certifies service dates and times for the juror's employer or school.</div>
        {prev.length > 0 && (
          <div className="max-h-16 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="jury-letter-prev">
            {prev.map((L) => (
              <div key={L.id} className="flex items-center gap-2 px-2 py-1 text-xs">
                <span className="font-mono font-bold text-ujms">{L.letter_number}</span>
                <span className="flex-1 truncate">{L.service_dates}</span>
                <button className="text-ujms underline" onClick={() => ujmsOpenJuryLetterPdf(L.id, L.letter_number)}
                  data-testid={`jury-letter-reprint-${L.letter_number}`}>reprint</button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Service date(s)" required>
            <input className={inputCls} value={dates} onChange={(e) => { setDates(e.target.value); setEdited(false); }}
              placeholder="08/24/2026 – 08/26/2026" data-testid="jury-letter-dates" />
          </Field>
          <Field label="Present from">
            <input type="time" className={inputCls} value={timeFrom} onChange={(e) => { setTimeFrom(e.target.value); setEdited(false); }} data-testid="jury-letter-from" />
          </Field>
          <Field label="Present until">
            <input type="time" className={inputCls} value={timeTo} onChange={(e) => { setTimeTo(e.target.value); setEdited(false); }} data-testid="jury-letter-to" />
          </Field>
        </div>
        <Field label="Presented to (employer / school)">
          <input className={inputCls} value={issuedTo} onChange={(e) => { setIssuedTo(e.target.value); setEdited(false); }}
            placeholder="optional" data-testid="jury-letter-issued-to" />
        </Field>
        <Field label="Letter body — edit freely before issuing" required>
          <textarea rows={8} className={`${inputCls} h-auto font-serif text-[13px] leading-relaxed`} value={body}
            onChange={(e) => { setBody(e.target.value); setEdited(true); }} data-testid="jury-letter-body" />
          {edited && <button className="text-xs text-ujms underline mt-1" onClick={() => setEdited(false)} data-testid="jury-letter-reset">Reset to standard template</button>}
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={busy || !dates.trim() || !body.trim()} onClick={submit} data-testid="jury-letter-submit">
            {busy ? "Issuing…" : "Issue & Print Letter"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InterpreterTab({ access }) {
  const [interpreters, setInterpreters] = useState([]);
  const [requests, setRequests] = useState([]);
  const [langs, setLangs] = useState([]);
  const [f, setF] = useState({});
  const manage = can(access, "service.manage");
  const load = useCallback(() => {
    api.get(`${U}/interpreters`).then((r) => setInterpreters(r.data)).catch(() => {});
    api.get(`${U}/interpreter-requests`).then((r) => setRequests(r.data)).catch(() => {});
    api.get(`${U}/interpreters/catalog`).then((r) => setLangs(r.data.languages)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed");
    }
  };
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="interpreter-tab">
      <Panel title="Certified interpreters" testId="interpreters-panel" action={manage && (
        <div className="flex gap-1.5">
          <input className={`${inputCls} w-36`} placeholder="Name…" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="interp-name-input" />
          <select className={selectCls} value={f.lang || ""} onChange={(e) => setF({ ...f, lang: e.target.value })} data-testid="interp-lang-select">
            <option value="">Language…</option>{langs.map((l) => <option key={l}>{l}</option>)}
          </select>
          <button className={btnPrimary} onClick={() => run(() => api.post(`${U}/interpreters`, { name: f.name, languages: [f.lang] }), "Interpreter added")} data-testid="interp-add-btn"><Languages className="w-4 h-4" /> Add</button>
        </div>)}>
        <table className="w-full border-collapse"><tbody>
          {interpreters.map((i) => (
            <tr key={i.id} data-testid={`interp-row-${i.id}`}><td className={tdCls}>{i.name}</td>
              <td className={`${tdCls} text-xs`}>{i.languages.join(", ")}</td></tr>
          ))}
          {!interpreters.length && <tr><td className={tdCls}>No interpreters registered.</td></tr>}
        </tbody></table>
      </Panel>
      <Panel title="Interpreter requests" testId="interp-requests-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Case</th><th className={thCls}>Language</th><th className={thCls}>Hearing</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
          <tbody>{requests.map((r) => (
            <tr key={r.id} data-testid={`interp-req-${r.id}`}>
              <td className={`${tdCls} font-mono text-xs`}>{r.case_number}</td>
              <td className={`${tdCls} text-xs`}>{r.language}</td>
              <td className={`${tdCls} font-mono text-xs`}>{(r.hearing_start || "").replace("T", " ").slice(0, 16)}{r.requeued_from && <span className="ml-1 text-[10px] text-amber-700 font-semibold">REQUEUED</span>}</td>
              <td className={tdCls}><Badge value={r.status} />{r.interpreter_name && <div className="text-xs text-slate-500">{r.interpreter_name}</div>}</td>
              <td className={tdCls}>
                {manage && r.status === "REQUESTED" && interpreters.filter((i) => i.languages.includes(r.language)).slice(0, 1).map((i) => (
                  <button key={i.id} className={btnGhost} onClick={() => run(() => api.post(`${U}/interpreter-requests/${r.id}/assign`, { interpreter_id: i.id }), `Assigned ${i.name}`)} data-testid={`interp-assign-${r.id}`}>Assign {i.name.split(" ")[0]}</button>
                ))}
              </td>
            </tr>
          ))}
          {!requests.length && <tr><td colSpan={5} className={tdCls}>No interpreter requests.</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

function ServiceTab({ access }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ service_type: "COURT_REPORTER" });
  const manage = can(access, "service.manage");
  const load = useCallback(() => api.get(`${U}/service-requests`).then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); } };
  return (
    <div className="space-y-4" data-testid="service-tab">
      <Panel title="New service request" testId="service-new">
        <div className="p-3 flex flex-wrap items-end gap-2">
          <Field label="Service">
            <select className={selectCls} value={f.service_type} onChange={(e) => setF({ ...f, service_type: e.target.value })} data-testid="svc-type-select">
              {["COURT_REPORTER", "INTERPRETER", "JURY", "ACCESSIBILITY", "TRANSPORT", "OTHER"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Case # (optional)"><input className={`${inputCls} w-36`} value={f.case_number || ""} onChange={(e) => setF({ ...f, case_number: e.target.value })} data-testid="svc-case-input" /></Field>
          <Field label="Details" className="flex-1"><input className={inputCls} value={f.details || ""} onChange={(e) => setF({ ...f, details: e.target.value })} data-testid="svc-details-input" /></Field>
          <button className={btnPrimary} onClick={() => run(() => api.post(`${U}/service-requests`, f), "Service request submitted")} data-testid="svc-submit-btn"><ConciergeBell className="w-4 h-4" /> Request</button>
        </div>
      </Panel>
      <Panel title="Requests" testId="service-list">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>#</th><th className={thCls}>Service</th><th className={thCls}>Case</th><th className={thCls}>Details</th><th className={thCls}>By</th><th className={thCls}>Assigned</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} data-testid={`svc-row-${r.request_number}`}>
              <td className={`${tdCls} font-mono text-xs`}>{r.request_number}</td>
              <td className={`${tdCls} text-xs`}>{r.service_type.replace(/_/g, " ")}</td>
              <td className={`${tdCls} font-mono text-xs`}>{r.case_number || "—"}</td>
              <td className={`${tdCls} text-xs max-w-xs truncate`}>{r.details}</td>
              <td className={`${tdCls} text-xs`}>{r.requested_by}</td>
              <td className={`${tdCls} text-xs`}>{r.assigned_to || "—"}</td>
              <td className={tdCls}><Badge value={r.status} /></td>
              <td className={`${tdCls} whitespace-nowrap`}>
                {manage && r.status === "REQUESTED" && <button className={btnGhost} onClick={() => run(() => api.patch(`${U}/service-requests/${r.id}`, { assigned_to: "Court Services Unit" }), "Assigned")} data-testid={`svc-assign-${r.request_number}`}>Assign</button>}
                {manage && r.status === "ASSIGNED" && <button className={`${btnGhost} ml-1`} onClick={() => run(() => api.patch(`${U}/service-requests/${r.id}`, { status: "COMPLETED" }), "Completed")} data-testid={`svc-complete-${r.request_number}`}>Complete</button>}
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={8} className={tdCls}>No service requests.</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
